import { useMemo } from 'react';
import { formatCurrency, formatNumber } from './format';
import { pointsToDollarsAt } from './hooks/usePricingRecalc';
import type { ProspectLoan, RateLockPriceAdjustment } from '../types';

interface PricingBreakdownLedgerProps {
  loan: ProspectLoan;
  loanAmount: number;
}

/**
 * The LLPA-expansion block: Base price → N expandable LLPA rows → Total LLPA →
 * Total price locked. Reads `loan.rate_lock_buy_side_adjustments` filtered to
 * `priceAdjustmentType === 'BasePrice'`. Does not depend on the legacy
 * `useGroupedAdjustments` helper — the grouping math is inlined here to keep
 * pricing-v2 self-contained.
 */
export function PricingBreakdownLedger({ loan, loanAmount }: PricingBreakdownLedgerProps) {
  const { basePricePts, llpas, totalLlpaPts, totalLockedPts } = useMemo(() => {
    const adjustments: RateLockPriceAdjustment[] = Array.isArray(loan.rate_lock_buy_side_adjustments)
      ? (loan.rate_lock_buy_side_adjustments as RateLockPriceAdjustment[])
      : [];
    const llpas = adjustments.filter((a) => a.priceAdjustmentType === 'BasePrice');
    const totalLlpaPts = llpas.reduce((sum, a) => sum + (Number(a.rate) || 0), 0);
    const totalLockedPts = Number(loan.rate_lock_total_buy_price) || 0;
    // Base price = Total locked − Total LLPA (LLPA rates are signed).
    const basePricePts = totalLockedPts - totalLlpaPts;
    return { basePricePts, llpas, totalLlpaPts, totalLockedPts };
  }, [loan.rate_lock_buy_side_adjustments, loan.rate_lock_total_buy_price]);

  const $ = (pts: number) => pointsToDollarsAt(loanAmount, pts);

  return (
    <>
      <div className="section-title">Pricing breakdown</div>
      <div className="ledger">
        <div className="ledger-head">
          <span>Description</span>
          <span className="num">Points</span>
          <span className="num">Dollars</span>
        </div>

        <div className="path-label base">
          <span className="bar" />
          <span>Base</span>
        </div>

        <div className="row">
          <div className="name">Base price</div>
          <div className="num tab emp">{formatNumber(basePricePts, 3)}</div>
          <div className="num tab" />
        </div>

        {/* LLPA rows are non-interactive: the only expansion detail was
            "Adjustment type" which was noise and was removed (QA follow-up).
            We also removed the expand button, role=button, tabIndex, and
            keyboard toggle so there's no dead affordance. If meaningful
            per-LLPA detail is ever restored, reinstate the chevron + toggle
            along with it. */}
        {llpas.map((adj, idx) => {
          const key = `llpa-${idx}`;
          const rate = Number(adj.rate) || 0;
          const sign = rate >= 0 ? 'pos' : 'neg';
          return (
            <div key={key} className="row">
              <div className="name">
                <span className="cap">LLPA</span>
                <span>{adj.description || 'Adjustment'}</span>
              </div>
              <div className={`num tab ${sign}`}>
                {rate >= 0 ? '+' : ''}
                {formatNumber(rate, 3)}
              </div>
              <div className={`num tab ${sign}`}>
                {rate >= 0 ? '+' : ''}
                {formatCurrency($(rate))}
              </div>
            </div>
          );
        })}

        <div className="row sub">
          <div className="name">Total LLPA</div>
          <div className={`num tab ${totalLlpaPts >= 0 ? 'pos' : 'neg'}`}>
            {totalLlpaPts >= 0 ? '+' : ''}
            {formatNumber(totalLlpaPts, 3)}
          </div>
          <div className={`num tab ${totalLlpaPts >= 0 ? 'pos' : 'neg'}`}>
            {totalLlpaPts >= 0 ? '+' : ''}
            {formatCurrency($(totalLlpaPts))}
          </div>
        </div>

        <div className="row total">
          <div className="name">Total price locked</div>
          <div className="num tab">{formatNumber(totalLockedPts, 3)}</div>
          <div className="num tab" />
        </div>
      </div>
    </>
  );
}
